import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import moment from 'moment-timezone';
import { WARNINGS_TYPE } from '@constants/index';
import { useTimezone } from '@hooks/useTimezone';
import CanvasTimeSeriesChart from '@components/Charts/CanvasTimeSeriesChart/CanvasTimeSeriesChart';
import { Stack, Typography } from '@esgian/esgianui';
import { useTheme } from '@hooks';
import { getRgbaColor } from '@components/Charts/ChartJsColorHelpers';
import { getUnixTimeStamp, getValue } from '@helpers';
import { orderChartElements } from '@helpers/chartHelpets';
import { NoDataAvailable } from '@components/Display';
const hpuNames = [
  'Actual HPU power available',
  'Actual HPU peak power',
  'Optimal HPU power available',
  'Optimal HPU power',
  'Actual online HPU',
  'Optimal online HPU'
];
const engNames = [
  'Power peak',
  'Average power produced',
  'Total power available',
  'Optimal power available',
  'Actual engines online',
  'Optimal engines online'
];
const agitatorsNames = [
  'Actual agitators power',
  'Actual agitators power available',
  'Optimal agitators power available',
  'Optimal agitators power',
  'Actual agitators running',
  'Optimal agitators running'
];

const availableMetrics = [
  {
    type: WARNINGS_TYPE.TooManyHPU,
    mainMetrics: [ 'peakPower', 'avgPowerProd', 'totPowerAvailable', 'optPowerAvailable' ],
    secondaryMetrics: [ 'engOn', 'optEngOn' ]
  },
  {
    type: WARNINGS_TYPE.TooManyEng,
    mainMetrics: [ 'peakPower', 'avgPowerProd', 'totPowerAvailable', 'optPowerAvailable' ],
    secondaryMetrics: [ 'engOn', 'optEngOn' ]
  },
  {
    type: WARNINGS_TYPE.TooManyAgitators,
    mainMetrics: [ 'peakPower', 'optPowerAvailable' ],
    secondaryMetrics: [ 'engOn', 'optEngOn' ]
  }
];

const getBackgroundColor = (value) => {
  if (value.opacity) {
    return getRgbaColor(value.color, value.opacity);
  }
  return 'rgba(0, 0, 0, 0.1)';
};

function WarningPowerDetailsChart({
  detailsData,
  rowData,
  loading,
  primaryTitle,
  secondaryTitle,
  unit,
  powerChartRef,
  onlineChartRef,
  powerSyncRefs,
  onlineSyncRefs
}) {
  const { selectedTimeZone } = useTimezone();
  const { theme } = useTheme();
  const {
    palette: {
      charts: { sixColorsLine, twoColorsContrast }
    }
  } = theme;
  let chartColors = [];
  let names = [];
  let secondarySeriesMaxY = 5;
  const rowDataType = rowData?.type;
  const isHPU = rowDataType === WARNINGS_TYPE.TooManyHPU;
  switch (rowDataType){

  }
  const availableMetricsForComponent = availableMetrics.find(m => m.type === rowDataType);
  switch (rowDataType){
    case WARNINGS_TYPE.TooManyHPU:
      names = hpuNames;
      secondarySeriesMaxY = detailsData?.numOfHPUs ?? secondarySeriesMaxY;
      chartColors = [
        { order: 2, color: sixColorsLine[3], opacity: undefined, showBackground: false },
        { order: 3, color: sixColorsLine[1], opacity: 0.4, showBackground: true },
        { order: 1, color: sixColorsLine[5], opacity: undefined, showBackground: true },
        { order: 4, color: sixColorsLine[2], opacity: 0.4, showBackground: true }
      ];
      break;
    case WARNINGS_TYPE.TooManyEng:
      names = engNames;
      secondarySeriesMaxY = detailsData?.numOfEngines ?? secondarySeriesMaxY;
      chartColors = [
        { order: 1, color: sixColorsLine[3], opacity: undefined, showBackground: false },
        { order: 2, color: sixColorsLine[1], opacity: 0.4, showBackground: true },
        { order: 4, color: sixColorsLine[4], opacity: 0.4, showBackground: true },
        { order: 3, color: sixColorsLine[5], opacity: 0.4, showBackground: true }
      ];
      break;
    case WARNINGS_TYPE.TooManyAgitators:
      names = agitatorsNames;
      secondarySeriesMaxY = detailsData?.numOfAgitators ?? secondarySeriesMaxY;
      chartColors = [
        { order: 2, color: sixColorsLine[1], opacity: 0.4, showBackground: true },
        { order: 3, color: sixColorsLine[3], opacity: undefined, showBackground: true },
        { order: 1, color: sixColorsLine[5], opacity: undefined, showBackground: true },
        { order: 4, color: sixColorsLine[2], opacity: 0.4, showBackground: true }
      ];
      break;
  };

  const { mainSeries = [], secondarySeries = [] } = useMemo(() => {
    if (!detailsData) return {};
    let startDate = moment.tz(rowData.startDate, selectedTimeZone).subtract(30, 'minute');
    let endDate = moment.tz(rowData.endDate, selectedTimeZone).add(30, 'minutes');
    let diff = endDate.diff(startDate, 'minute');
    const { peakPower, avgPowerProd, totPowerAvailable, optPowerAvailable, engOn, optEngOn } =
      detailsData;
    let main = [];
    if(availableMetricsForComponent.mainMetrics.includes('peakPower'))
      main.push({
        order: chartColors[0].order,
        spanGaps: false,
        label: names[0],
        backgroundColor: chartColors[0].showBackground ? getBackgroundColor(chartColors[0]) : undefined,
        borderColor: chartColors[0].color,
        type: 'line',
        fill: chartColors[0].showBackground ? 'origin' : false,
        data: Object.keys(peakPower)?.map((key) => {
          let value = getValue(peakPower[key], false);
          return { x: getUnixTimeStamp(key).valueOf(), y: value };
        })
      });
    if(availableMetricsForComponent.mainMetrics.includes('avgPowerProd'))
      main.push({
        order: chartColors[1].order,
        spanGaps: false,
        label: names[1],
        backgroundColor: chartColors[1].showBackground ? getBackgroundColor(chartColors[1]) : undefined,
        type: 'line',
        fill: chartColors[0].showBackground ? 'origin' : false,
        borderColor: chartColors[1].color,
        data: Object.keys(avgPowerProd)?.map((key) => {
          let value = getValue(avgPowerProd[key], false);
          return { x: getUnixTimeStamp(key).valueOf(), y: value };
        })
      });
    if(availableMetricsForComponent.mainMetrics.includes('totPowerAvailable'))
      main.push({
        order: chartColors[2].order,
        spanGaps: false,
        type: 'line',
        label: names[2],
        borderColor: chartColors[2].color,
        fill: chartColors[0].showBackground ? (isHPU ? false : 'origin') : false,
        fill: isHPU ? false : 'origin',
        backgroundColor: chartColors[1].showBackground ? getBackgroundColor(chartColors[2]) : undefined,
        data: Object.keys(totPowerAvailable)?.map((key, i) => {
          let value = getValue(totPowerAvailable[key], false);
          if (isHPU && (i <= 29 || i >= diff - 29)) {
            return { x: getUnixTimeStamp(key).valueOf(), y: null };
          }
          return { x: getUnixTimeStamp(key).valueOf(), y: value };
        })
      });
    if(availableMetricsForComponent.mainMetrics.includes('optPowerAvailable'))
      main.push({
      order: chartColors[3].order,
      label: names[3],
      spanGaps: false,
      type: 'line',
      borderColor: chartColors[3].color,
      fill: chartColors[0].showBackground ? 'origin' : false,
      backgroundColor: chartColors[1].showBackground ? getBackgroundColor(chartColors[3]) : undefined,
      data: Object.keys(optPowerAvailable)?.map((key, i) => {
        let value = getValue(optPowerAvailable[key], false);
        if (i <= 29 || i >= diff - 29) {
          return { x: getUnixTimeStamp(key).valueOf(), y: null };
        }

        return { x: getUnixTimeStamp(key).valueOf(), y: value };
      })
    });
    let sec = [];
    if(availableMetricsForComponent.secondaryMetrics.includes('engOn'))
      sec.push({
        order: 2,
        label: names[4],
        borderColor: twoColorsContrast[0],
        spanGaps: false,
        fill: 'origin',
        backgroundColor: getBackgroundColor({ color: twoColorsContrast[0], opacity: 0.4 }),
        data: Object.keys(engOn)?.map((key) => {
          let value = getValue(engOn[key], false);
          return { x: getUnixTimeStamp(key).valueOf(), y: value };
        })
      })
    if(availableMetricsForComponent.secondaryMetrics.includes('optEngOn'))
      sec.push({
        order: 1,
        label: names[5],
        spanGaps: false,
        borderColor: twoColorsContrast[1],
        fill: 'origin',
        backgroundColor: getBackgroundColor({ color: twoColorsContrast[1], opacity: 0.4 }),
        data: Object.keys(optEngOn)?.map((key, i) => {
          let value = getValue(optEngOn[key], false);
          if (i <= 29 || i >= diff - 29) {
            return { x: getUnixTimeStamp(key).valueOf(), y: null };
          }

          return { x: getUnixTimeStamp(key).valueOf(), y: value };
        })
      });

    if (rowDataType === WARNINGS_TYPE.TooManyEng) {
      main = orderChartElements([main[2], main[1], main[0], main[3]]);
    }
    main = main.filter(item => item?.data !== undefined);
    return {
      mainSeries: orderChartElements(main),
      secondarySeries: orderChartElements(sec)
    };
  }, [detailsData, rowData]);

  return (
    <Stack spacing={2}>
      <Stack>
        {primaryTitle && (
          <Typography variant={'subtitle1'} bold sx={{ pb: 2 }}>
            {primaryTitle}
          </Typography>
        )}
        {!loading && NoDataAvailable.hasNoData(mainSeries) ? (
          <NoDataAvailable height="175px" />
        ) : (
        <CanvasTimeSeriesChart
          loading={loading}
          unit={unit}
          height={'175px'}
          syncRefs={powerSyncRefs}
          chartRef={powerChartRef}
          series={mainSeries}
          id={'first-chart'}
        />
        )}
      </Stack>
      <Stack>
        <Typography variant={'subtitle1'} bold sx={{ pb: 2 }}>
          {secondaryTitle}
        </Typography>
        {!loading && NoDataAvailable.hasNoData(secondarySeries) ? (
          <NoDataAvailable height="175px" />
        ) : (
        <CanvasTimeSeriesChart
          maxY={secondarySeriesMaxY}
          calculateYMax={false}
          loading={loading}
          height={'175px'}
          chartRef={onlineChartRef}
          syncRefs={onlineSyncRefs}
          series={secondarySeries}
          id={'sec-chart'}
        />
        )}
      </Stack>
    </Stack>
  );
}

WarningPowerDetailsChart.propTypes = {
  detailsData: PropTypes.object,
  rowData: PropTypes.object,
  unit: PropTypes.string,
  loading: PropTypes.bool,
  primaryTitle: PropTypes.object,
  secondaryTitle: PropTypes.object,
  powerChartRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })])
    .isRequired,
  onlineChartRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })])
    .isRequired,
  powerSyncRefs: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })])
  ).isRequired,
  onlineSyncRefs: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })])
  ).isRequired
};

WarningPowerDetailsChart.defaultProps = {
  detailsData: null,
  primaryTitle: null,
  secondaryTitle: null,
  unit: '',
  rowData: null,
  loading: false
};

export default WarningPowerDetailsChart;
